import type { Stores } from '../../../../../../util/store'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import styles from './model-wizard.module.scss'
import { Button, Chip } from '@mui/material'
import { ChevronLeft } from '@mui/icons-material'

interface WOModelAutoMatchProps {
    stores: Stores
}
export const WOModelMatchWizard = observer(
    ({ stores }: WOModelAutoMatchProps) => {
        const [currentIndex, setCurrentIndex] = useState(0)
        const [newAlias, setNewAlias] = useState('')
        const [newAliasModelId, setNewAliasModelId] = useState<
            string | undefined
        >(undefined)
        const { unmatchedModels, loading, createModelAlias, modelPagination } =
            stores.manufacturerModelMatch

        useEffect(() => {
            if (unmatchedModels.length - currentIndex < 10) {
                stores.manufacturerModelMatch.load()
            }
        }, [
            currentIndex,
            unmatchedModels.length,
            stores.manufacturerModelMatch,
        ])
        const currentModel = useMemo(
            () => unmatchedModels[currentIndex],
            [currentIndex, unmatchedModels],
        )
        const handleSkipOnClick = useCallback(() => {
            setNewAlias('')
            setNewAliasModelId(undefined)
            setCurrentIndex(currentIndex + 1)
        }, [currentIndex])
        const handleMatchOnClick = useCallback(() => {
            if (newAliasModelId && currentModel?.existing_model_aliases[0].id) {
                createModelAlias(
                    currentModel?.existing_model_aliases[0].id,
                    newAliasModelId,
                )
                    .then(() => {
                        handleSkipOnClick()
                    })
                    .catch((e) => {
                        console.error('Failed to match', e)
                    })
            } else {
                console.error(
                    'Failed to match, No model alias found, so update does not work',
                )
            }
        }, [
            currentModel?.existing_model_aliases,
            handleSkipOnClick,
            createModelAlias,
            newAliasModelId,
        ])

        const existingAliasSuggestions = () => {
            const existingAliases = currentModel?.existing_model_aliases
            if (!existingAliases || existingAliases.length === 0) return null
            return (
                <div className={styles.suggestionWrapper}>
                    <p className={styles.smallHeader}>Existing Aliases: </p>
                    {existingAliases.map((alias) => {
                        return (
                            <Chip
                                onClick={() => {
                                    setNewAlias(alias.product_model?.name ?? '')
                                    setNewAliasModelId(alias.product_model?.id)
                                }}
                                className={styles.suggestionOption}
                                label={
                                    alias.product_model?.name ??
                                    `NULL (alias id: ${alias.id})`
                                }
                            />
                        )
                    })}
                </div>
            )
        }

        const regexSuggestions = useCallback(() => {
            const regexMatches = currentModel?.regex_model_matches
            if (!regexMatches || regexMatches.length === 0) return null
            return (
                <div className={styles.suggestionWrapper}>
                    <p className={styles.smallHeader}>Regex Matches: </p>
                    {regexMatches.map((regexMatch) => {
                        return (
                            <Chip
                                onClick={() => {
                                    setNewAlias(regexMatch.name)
                                    setNewAliasModelId(regexMatch.id)
                                }}
                                className={styles.suggestionOption}
                                label={regexMatch.name}
                            />
                        )
                    })}
                </div>
            )
        }, [currentModel?.regex_model_matches])
        if (!currentModel) return <div>Loading...</div>
        return (
            <div data-testid="org-wo-mm-model-wizard-page" className={styles.container}>
                <div className={styles.summaryWrapper}>
                    {currentIndex > 0 && (
                        <div className={styles.summaryItem}>
                            <p className={styles.smallHeader}>
                                <Button
                                    className={styles.button}
                                    onClick={() =>
                                        setCurrentIndex(currentIndex - 1)
                                    }>
                                    <ChevronLeft />
                                </Button>
                            </p>
                        </div>
                    )}
                    <div className={styles.summaryItem}>
                        <p className={styles.smallHeader}>
                            {`Input Model Name: (#${currentIndex + 1}/${modelPagination.total})`}
                        </p>
                        <div className={styles.header}>
                            <span>{currentModel.name}</span>
                        </div>
                    </div>
                    <div className={styles.summaryItem}>
                        <p className={styles.smallHeader}>
                            Work Order Segment Count:
                        </p>
                        <div className={styles.header}>
                            {currentModel.work_order_segments_count}
                        </div>
                    </div>
                    <div className={styles.summaryItem}>
                        <p
                            className={
                                styles.smallHeader
                            }>{`Input Manufacturer Name: `}</p>
                        <div className={styles.header}>
                            {currentModel.input_manufacturer_name}
                        </div>
                    </div>
                </div>
                <div className={styles.suggestionContainer}>
                    {existingAliasSuggestions()}
                    {regexSuggestions()}
                </div>
                <div className={styles.footer}>
                    <div className={styles.footerInputWrapper}>
                        <p className={styles.smallHeader}>
                            Matching Model Name:
                        </p>
                        <input
                            className={styles.footerInput}
                            type="text"
                            value={newAlias}
                            onChange={(e) => {
                                setNewAlias(e.target.value)
                            }}
                        />
                        <p className={styles.smallHeader}>
                            {`Corresponding Model ID: ${newAliasModelId ?? 'not found'}`}
                        </p>
                    </div>
                    <Button
                        disabled={
                            currentIndex === unmatchedModels.length - 1 &&
                            loading
                        }
                        className={styles.footerButton}
                        onClick={handleSkipOnClick}>
                        Skip
                    </Button>
                    <Button
                        disabled={loading || !newAliasModelId}
                        className={styles.footerButton}
                        onClick={handleMatchOnClick}>
                        {loading ? 'Matching...' : 'Match'}
                    </Button>
                </div>
            </div>
        )
    },
)
